import React from 'react';
import { ID, SVGProps, UserAction, UserActionState } from '@repo/utils/types';
import Group from '@repo/utils/models/Group';
import {
  ActionCopyIcon,
  PenIcon,
  ActionPlusIcon,
  ActionRemoveIcon,
  DownloadIcon,
  ShareActionIcon,
} from '@repo/icons';
import { Dispatch } from 'redux';
import { downloadGroupCSV, promptToDeleteGroup } from '@repo/redux/actions/groups';
import * as actions from '@repo/redux/actions/groupView';
import { NavigateFunction } from 'react-router-dom';
import { openModal } from '@repo/redux/actions/ui';
import Contact from '@repo/utils/models/Contact';
import { GetEntitlementValueFn } from '@repo/utils/context';
import { ReconnectIcon } from '@repo/icons/Reconnect';

export const removeContactsFromGroup = (
  group: Group,
  selectedIDs: Immutable.Set<ID>,
  dispatch: Dispatch,
) => {
  dispatch(actions.updateGroupInView(group.removeContactIDSet(selectedIDs)));
};

const buildGroupActions = (
  userActionState: UserActionState,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  userIsAdmin: boolean,
  getEntitlementValue: GetEntitlementValueFn,
  contactIsRestricted: (contact: Contact) => boolean,
  getContact: (id: ID) => Contact | undefined,
  includeSharing: boolean = false,
): Array<UserAction> => {
  const group = userActionState.groupView?.group;
  const isShared = userActionState.groupView?.isShared;
  const canExport = userIsAdmin || !!getEntitlementValue('groups.export');

  // TODO: group.isImporting isn't a reliable check for whether the group is currently importing...
  //  should switch to using the import status (selectImportStatus) instead
  if (!group || group?.isImporting) {
    return [];
  }

  const groupActions = [
    {
      title: 'Edit Group',
      type: 'action',
      action: () => dispatch(actions.startEditing),
      icon: PenIcon,
      keyboard: 'e',
      disabled: group.is_internal || !group.isPersisted,
    },
    {
      title: 'Add People',
      type: 'action',
      action: () => dispatch(actions.startContactEditing),
      icon: ActionPlusIcon,
      keyboard: 'a',
    },
    {
      title: 'Set Reconnect Cadence',
      type: 'action',
      action: () => dispatch(openModal('groupReconnect', { groupID: group.id })),
      icon: (props: SVGProps) => <ReconnectIcon {...props} group={group} />,
    },
    {
      title: 'Copy email addresses',
      type: 'action',
      action: group.copyAllEmails(contactIsRestricted, getContact),
      icon: ActionCopyIcon,
      disabled: group.memberIDs.size === 0,
    },
    {
      title: 'Copy phone numbers',
      type: 'action',
      action: group.copyAllPhoneNumbers(contactIsRestricted, getContact),
      icon: ActionCopyIcon,
      disabled: group.memberIDs.size === 0,
    },
    {
      title: 'Download CSV',
      type: 'action',
      action: () => downloadGroupCSV(group, dispatch),
      icon: DownloadIcon,
      disabled: !canExport || group.memberIDs.size === 0,
    },
    {
      title: 'Delete Group',
      type: 'action',
      action: () => promptToDeleteGroup(group, dispatch, navigate),
      icon: ActionRemoveIcon,
      disabled: group.is_internal,
    },
  ];

  if (includeSharing) {
    groupActions.push({
      title: 'Share Group',
      type: 'action',
      action: () => dispatch(openModal('shareGroup')),
      icon: (props) => <ShareActionIcon isShared={isShared} {...props} />,
      disabled: false,
    });
  }

  return groupActions.filter(({ disabled }) => !disabled) as Array<UserAction>;
};

export default buildGroupActions;
